import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Home, Car, Wallet, Building2, Zap, Timer, IndianRupee,
  Calculator, TrendingUp, Sparkles, MapPin, ShieldCheck, Users,
  Landmark, ChevronRight, Award, Trophy,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/* ==========================================================================
 * Hero Product Cards — 4 primary loan products, each with an "Apply Now"
 * that deep-links straight into the Typeform-style wizard.
 * ==========================================================================
 */

const HERO_PRODUCTS = [
  {
    key: "home_loan",
    slug: "home-loan",
    title: "Home Loan",
    desc: "Instant approval at lowest interest rates.",
    badge: "Quick Disbursal",
    icon: Home,
    accent: "#7A4E1D",
  },
  {
    key: "personal_loan",
    slug: "personal-loan",
    title: "Personal Loan",
    desc: "Paperless process at low rate.",
    badge: "Lowest EMI Ride",
    icon: Wallet,
    accent: "#B8860B",
  },
  {
    key: "car_loan",
    slug: "car-loan",
    title: "New Car Loan",
    desc: "Drive away your dream car today.",
    badge: "Rewards Unlimited",
    icon: Car,
    accent: "#5A3910",
  },
  {
    key: "lap",
    slug: "lap",
    title: "Loan Against Property",
    desc: "Unlock the value of your property.",
    badge: "High-Ticket",
    icon: Building2,
    accent: "#8A6600",
  },
];

export function HeroProductCards() {
  return (
    <section className="brand-section-alt border-b border-[#B8860B]/20" data-testid="raise-debt-hero-cards">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[10px] font-mono tracking-widest uppercase text-[#7A4E1D] mb-2">HERO PRODUCTS · APPLY IN 90 SECONDS</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">Popular loans, wizard-style.</h2>
          </div>
          <Link to="/venturaz-money/products" className="text-sm font-heading text-[#7A4E1D] hover:underline inline-flex items-center gap-1" data-testid="hero-cards-view-all">
            View all 15 products <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HERO_PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="group relative rounded-2xl border border-[#B8860B]/25 bg-white p-5 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(31,20,8,0.10)] transition-all"
                data-testid={`hero-product-card-${p.key}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-lg inline-flex items-center justify-center text-[#FFF3D9]" style={{ background: p.accent }}>
                    <Icon size={20} />
                  </div>
                  <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-gold/25 text-[#8A6600] border border-gold/50">
                    {p.badge}
                  </span>
                </div>
                <div className="font-display text-xl text-ink leading-tight">{p.title}</div>
                <p className="mt-1.5 text-sm text-ink-soft leading-relaxed">{p.desc}</p>
                <Link
                  to={`/money/apply/${p.key}?src=hero-product-card`}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-bold hover:bg-[#5c3a15] transition-colors"
                  data-testid={`hero-product-card-apply-${p.key}`}
                >
                  Apply Now <ArrowRight size={13} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
 * Financial Calculators — grid of 4 calculators, result gated behind
 * phone + email capture (creates a lead with source_section=`calculator-*`).
 * ==========================================================================
 */

const CALCULATORS = [
  {
    key: "emi",
    title: "EMI Calculator",
    desc: "Monthly EMI, total interest, total payable.",
    icon: Calculator,
    inputs: [
      { key: "amount_inr", label: "Loan amount (₹)", min: 100000, max: 500000000, step: 100000, value: 5000000 },
      { key: "rate", label: "Interest rate (% p.a.)", min: 7, max: 22, step: 0.1, value: 10.5 },
      { key: "tenure_months", label: "Tenure (months)", min: 6, max: 360, step: 6, value: 60 },
    ],
    compute: ({ amount_inr, rate, tenure_months }) => {
      const r = rate / 12 / 100;
      const n = tenure_months;
      const emi = r === 0 ? amount_inr / n : (amount_inr * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      return [
        { k: "Monthly EMI", v: `₹${Math.round(emi).toLocaleString("en-IN")}`, highlight: true },
        { k: "Total interest", v: `₹${Math.round(emi * n - amount_inr).toLocaleString("en-IN")}` },
        { k: "Total payable", v: `₹${Math.round(emi * n).toLocaleString("en-IN")}` },
      ];
    },
  },
  {
    key: "home_eligibility",
    title: "Home Loan Eligibility",
    desc: "How much home loan you can qualify for.",
    icon: Home,
    inputs: [
      { key: "monthly_income", label: "Monthly income (₹)", min: 25000, max: 1000000, step: 5000, value: 150000 },
      { key: "monthly_obligations", label: "Existing EMIs (₹)", min: 0, max: 500000, step: 1000, value: 10000 },
      { key: "tenure_months", label: "Tenure (months)", min: 60, max: 360, step: 12, value: 240 },
      { key: "rate", label: "Assumed rate (% p.a.)", min: 7, max: 12, step: 0.1, value: 8.5 },
    ],
    compute: ({ monthly_income, monthly_obligations, tenure_months, rate }) => {
      // Foir 45% of net cashflow available for new EMI.
      const cashflow = Math.max(0, monthly_income * 0.45 - monthly_obligations);
      const r = rate / 12 / 100;
      const n = tenure_months;
      const principal = r === 0 ? cashflow * n : cashflow * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
      return [
        { k: "Eligible loan", v: `₹${Math.round(principal).toLocaleString("en-IN")}`, highlight: true },
        { k: "Comfortable EMI", v: `₹${Math.round(cashflow).toLocaleString("en-IN")}` },
        { k: "FOIR used", v: "45%" },
      ];
    },
  },
  {
    key: "personal_eligibility",
    title: "Personal Loan Eligibility",
    desc: "Max unsecured loan against your salary.",
    icon: Wallet,
    inputs: [
      { key: "monthly_income", label: "Monthly income (₹)", min: 25000, max: 1000000, step: 5000, value: 80000 },
      { key: "monthly_obligations", label: "Existing EMIs (₹)", min: 0, max: 500000, step: 1000, value: 5000 },
      { key: "tenure_months", label: "Tenure (months)", min: 12, max: 60, step: 6, value: 36 },
      { key: "rate", label: "Assumed rate (% p.a.)", min: 10, max: 22, step: 0.1, value: 12.5 },
    ],
    compute: ({ monthly_income, monthly_obligations, tenure_months, rate }) => {
      const cashflow = Math.max(0, monthly_income * 0.55 - monthly_obligations);
      const r = rate / 12 / 100;
      const n = tenure_months;
      const principal = r === 0 ? cashflow * n : cashflow * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
      return [
        { k: "Eligible loan", v: `₹${Math.round(principal).toLocaleString("en-IN")}`, highlight: true },
        { k: "Comfortable EMI", v: `₹${Math.round(cashflow).toLocaleString("en-IN")}` },
        { k: "FOIR used", v: "55%" },
      ];
    },
  },
  {
    key: "balance_transfer",
    title: "Balance Transfer Savings",
    desc: "How much you'd save by moving your loan.",
    icon: TrendingUp,
    inputs: [
      { key: "outstanding", label: "Outstanding principal (₹)", min: 500000, max: 500000000, step: 100000, value: 5000000 },
      { key: "current_rate", label: "Current rate (%)", min: 7, max: 22, step: 0.1, value: 10.5 },
      { key: "new_rate", label: "New rate (%)", min: 7, max: 22, step: 0.1, value: 8.5 },
      { key: "remaining_months", label: "Remaining months", min: 12, max: 360, step: 6, value: 180 },
    ],
    compute: ({ outstanding, current_rate, new_rate, remaining_months }) => {
      const emi = (p, ratePct, n) => {
        const r = ratePct / 12 / 100;
        return r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      };
      const currentEmi = emi(outstanding, current_rate, remaining_months);
      const newEmi = emi(outstanding, new_rate, remaining_months);
      const monthly = currentEmi - newEmi;
      const total = monthly * remaining_months;
      return [
        { k: "Monthly savings", v: `₹${Math.round(monthly).toLocaleString("en-IN")}`, highlight: true },
        { k: "Total savings", v: `₹${Math.round(total).toLocaleString("en-IN")}` },
        { k: "New EMI", v: `₹${Math.round(newEmi).toLocaleString("en-IN")}` },
      ];
    },
  },
];

export function FinancialCalculators() {
  const [active, setActive] = useState("emi");
  const cfg = CALCULATORS.find((c) => c.key === active) || CALCULATORS[0];

  return (
    <section id="calculators" className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-financial-calcs">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="mb-8 max-w-3xl">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">
            FINANCIAL CALCULATORS · INSTANT MATH, ADVISOR-BACKED
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            Four calculators. One free consultation.
          </h2>
          <p className="mt-3 text-ink-soft text-base leading-relaxed">
            Adjust the sliders on the left, share your name &amp; phone once, and we&apos;ll unlock the numbers &mdash;
            plus a callback from a specialist to help you interpret them.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left rail — calculator picker */}
          <div className="lg:col-span-1 space-y-2" data-testid="calc-picker">
            {CALCULATORS.map((c) => {
              const Icon = c.icon;
              const on = c.key === active;
              return (
                <button
                  key={c.key}
                  onClick={() => setActive(c.key)}
                  className={`w-full text-left rounded-lg p-3 border transition-colors ${on ? "bg-[#7A4E1D] text-[#FFF3D9] border-[#7A4E1D]" : "bg-white text-ink border-ink/10 hover:border-[#B8860B]/50"}`}
                  data-testid={`calc-tab-${c.key}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} className={on ? "text-gold" : "text-[#7A4E1D]"} />
                    <div className="font-heading text-sm">{c.title}</div>
                  </div>
                  <div className={`text-[11px] mt-0.5 leading-relaxed ${on ? "text-[#FFF3D9]/70" : "text-ink-muted"}`}>{c.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Active calculator */}
          <div className="lg:col-span-3">
            <ActiveCalc cfg={cfg} />
          </div>
        </div>
      </div>
    </section>
  );
}

function ActiveCalc({ cfg }) {
  // Local slider state — resets on tab change (via `key={cfg.key}` in parent isn't set; use derived reset via useMemo).
  const initial = useMemo(() => Object.fromEntries(cfg.inputs.map((i) => [i.key, i.value])), [cfg.key]);
  const [vals, setVals] = useState(initial);
  React.useEffect(() => { setVals(initial); }, [initial]);

  const [unlocked, setUnlocked] = useState(false);
  const [lead, setLead] = useState({ contact_name: "", contact_phone: "", contact_email: "", city: "" });
  const [busy, setBusy] = useState(false);

  const results = useMemo(() => cfg.compute(vals), [cfg, vals]);
  const set = (k) => (e) => setVals((s) => ({ ...s, [k]: Number(e.target.value) }));

  const unlock = async () => {
    if (!lead.contact_name || !lead.contact_phone || !lead.contact_email) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/debt-leads", {
        source_page: "/money",
        source_section: `calculator-${cfg.key}`,
        contact_name: lead.contact_name,
        contact_phone: lead.contact_phone,
        contact_email: lead.contact_email,
        city: lead.city,
        amount_inr: vals.amount_inr || vals.outstanding || null,
        tenure_months: vals.tenure_months || vals.remaining_months || null,
        extra: { calculator_inputs: vals, calculator_results: results.map((r) => ({ k: r.k, v: r.v })) },
      });
      toast.success("Numbers unlocked. A specialist will reach out shortly.");
      setUnlocked(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#B8860B]/25 bg-white p-6 md:p-8" data-testid={`calc-panel-${cfg.key}`}>
      <div className="flex items-center gap-2 mb-4">
        <cfg.icon size={16} className="text-[#7A4E1D]" />
        <div className="font-display text-2xl text-ink">{cfg.title}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {cfg.inputs.map((i) => (
          <div key={i.key}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{i.label}</label>
              <div className="font-heading text-sm text-[#7A4E1D]" data-testid={`calc-${cfg.key}-${i.key}-val`}>
                {i.label.includes("%") ? `${Number(vals[i.key]).toFixed(2)}%` : Number(vals[i.key]).toLocaleString("en-IN")}
              </div>
            </div>
            <input
              type="range"
              min={i.min} max={i.max} step={i.step}
              value={vals[i.key]}
              onChange={set(i.key)}
              className="w-full accent-[#7A4E1D]"
              data-testid={`calc-${cfg.key}-${i.key}`}
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {results.map((r, i) => (
          <div key={i} className={`rounded-lg p-4 text-center ${r.highlight ? "bg-[#7A4E1D] text-[#FFF3D9]" : "bg-[#FBFAF5] border border-[#B8860B]/20 text-ink"}`}>
            <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${r.highlight ? "text-[#FFF3D9]/70" : "text-ink-muted"}`}>{r.k}</div>
            <div className={`font-display text-xl leading-tight ${unlocked ? "" : "blur-md select-none"}`} data-testid={`calc-${cfg.key}-result-${i}`}>
              {r.v}
            </div>
          </div>
        ))}
      </div>

      {!unlocked && (
        <div className="rounded-lg border border-gold/40 bg-gold/10 p-4" data-testid={`calc-gate-${cfg.key}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] mb-2 inline-flex items-center gap-1">
            <Sparkles size={11} /> Unlock the numbers &middot; free consultation included
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Your full name" value={lead.contact_name} onChange={(e) => setLead((s) => ({ ...s, contact_name: e.target.value }))}
              className="px-3 py-2 border border-ink/15 rounded text-sm bg-white focus:outline-none focus:border-[#7A4E1D]" data-testid={`calc-gate-${cfg.key}-name`} />
            <input placeholder="Mobile" value={lead.contact_phone} onChange={(e) => setLead((s) => ({ ...s, contact_phone: e.target.value }))}
              className="px-3 py-2 border border-ink/15 rounded text-sm bg-white focus:outline-none focus:border-[#7A4E1D]" data-testid={`calc-gate-${cfg.key}-phone`} />
            <input placeholder="Email" value={lead.contact_email} onChange={(e) => setLead((s) => ({ ...s, contact_email: e.target.value }))}
              className="px-3 py-2 border border-ink/15 rounded text-sm bg-white focus:outline-none focus:border-[#7A4E1D]" data-testid={`calc-gate-${cfg.key}-email`} />
            <input placeholder="City (optional)" value={lead.city} onChange={(e) => setLead((s) => ({ ...s, city: e.target.value }))}
              className="px-3 py-2 border border-ink/15 rounded text-sm bg-white focus:outline-none focus:border-[#7A4E1D]" data-testid={`calc-gate-${cfg.key}-city`} />
          </div>
          <button
            onClick={unlock} disabled={busy}
            className="mt-3 w-full md:w-auto px-5 py-2.5 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-bold hover:bg-[#5c3a15] disabled:opacity-60"
            data-testid={`calc-gate-${cfg.key}-submit`}
          >
            {busy ? "Unlocking…" : "Show my numbers →"}
          </button>
        </div>
      )}
      {unlocked && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-900 flex items-start gap-2" data-testid={`calc-unlocked-${cfg.key}`}>
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />
          Numbers unlocked. A named debt specialist will call you within 1 business day.
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * Banking Partners — 100+ logos grid. Uses Clearbit's public logo service
 * which is free & CDN-hosted; graceful fallback to a monogram tile if the
 * logo can't be fetched (e.g. corporate proxy).
 * ==========================================================================
 */

const BANK_PARTNERS = [
  { name: "State Bank of India", domain: "sbi.co.in", tier: "top" },
  { name: "HDFC Bank", domain: "hdfcbank.com", tier: "top" },
  { name: "ICICI Bank", domain: "icicibank.com", tier: "top" },
  { name: "Axis Bank", domain: "axisbank.com", tier: "top" },
  { name: "Kotak Mahindra Bank", domain: "kotak.com", tier: "top" },
  { name: "IndusInd Bank", domain: "indusind.com", tier: "top" },
  { name: "Bajaj Finserv", domain: "bajajfinserv.in", tier: "top" },
  { name: "Tata Capital", domain: "tatacapital.com", tier: "top" },
  { name: "Yes Bank", domain: "yesbank.in" },
  { name: "IDFC First Bank", domain: "idfcfirstbank.com" },
  { name: "Federal Bank", domain: "federalbank.co.in" },
  { name: "RBL Bank", domain: "rblbank.com" },
  { name: "Bank of Baroda", domain: "bankofbaroda.in" },
  { name: "Punjab National Bank", domain: "pnbindia.in" },
  { name: "Union Bank of India", domain: "unionbankofindia.co.in" },
  { name: "Canara Bank", domain: "canarabank.com" },
  { name: "Bank of India", domain: "bankofindia.co.in" },
  { name: "Indian Bank", domain: "indianbank.in" },
  { name: "Aditya Birla Capital", domain: "adityabirlacapital.com" },
  { name: "L&T Finance", domain: "ltfs.com" },
  { name: "Piramal Capital", domain: "piramalfinance.com" },
  { name: "Hero FinCorp", domain: "herofincorp.com" },
  { name: "Cholamandalam", domain: "cholamandalam.com" },
  { name: "Mahindra Finance", domain: "mahindrafinance.com" },
  { name: "Shriram Finance", domain: "shriramfinance.in" },
  { name: "Muthoot Finance", domain: "muthootfinance.com" },
  { name: "Manappuram Finance", domain: "manappuram.com" },
  { name: "IIFL Finance", domain: "iiflfinance.com" },
  { name: "Poonawalla Fincorp", domain: "poonawallafincorp.com" },
  { name: "Fullerton India", domain: "fullertonindia.com" },
  { name: "Edelweiss", domain: "edelweissfin.com" },
  { name: "JM Financial", domain: "jmfl.com" },
  { name: "Lendingkart", domain: "lendingkart.com" },
  { name: "InCred", domain: "incred.com" },
  { name: "DMI Finance", domain: "dmifinance.in" },
  { name: "Godrej Capital", domain: "godrejcapital.com" },
  { name: "U GRO Capital", domain: "ugrocapital.com" },
  { name: "Standard Chartered", domain: "sc.com" },
  { name: "HSBC India", domain: "hsbc.co.in" },
  { name: "DBS Bank India", domain: "dbs.com" },
  { name: "Citi India", domain: "citi.com" },
  { name: "Deutsche Bank India", domain: "db.com" },
  { name: "AU Small Finance Bank", domain: "aubank.in" },
  { name: "Ujjivan SFB", domain: "ujjivansfb.in" },
  { name: "Equitas SFB", domain: "equitasbank.com" },
  { name: "ESAF SFB", domain: "esafbank.com" },
  { name: "Jana SFB", domain: "janabank.com" },
  { name: "Bandhan Bank", domain: "bandhanbank.com" },
  { name: "CSB Bank", domain: "csb.co.in" },
  { name: "Karnataka Bank", domain: "karnatakabank.com" },
  { name: "South Indian Bank", domain: "southindianbank.com" },
  { name: "City Union Bank", domain: "cityunionbank.com" },
  { name: "Karur Vysya Bank", domain: "kvb.co.in" },
  { name: "LIC Housing Finance", domain: "lichousing.com" },
  { name: "PNB Housing", domain: "pnbhousing.com" },
  { name: "Bajaj Housing Finance", domain: "bajajhousingfinance.in" },
  { name: "Aavas Financiers", domain: "aavas.in" },
  { name: "Home First Finance", domain: "homefirstindia.com" },
  { name: "Aptus Value Housing", domain: "aptusindia.com" },
  { name: "Repco Home Finance", domain: "repcohome.com" },
  { name: "IIFL Home Finance", domain: "iiflhomeloans.com" },
  { name: "India Shelter", domain: "indiashelter.in" },
  { name: "HDFC Credila", domain: "hdfccredila.com" },
  { name: "Avanse", domain: "avanse.com" },
  { name: "Auxilo", domain: "auxilo.com" },
  { name: "Prodigy Finance", domain: "prodigyfinance.com" },
  { name: "MPower Financing", domain: "mpowerfinancing.com" },
  { name: "Kinara Capital", domain: "kinaracapital.com" },
  { name: "Aye Finance", domain: "ayefin.com" },
  { name: "Protium Finance", domain: "protium.co.in" },
  { name: "Northern Arc", domain: "northernarc.com" },
  { name: "Vivriti Capital", domain: "vivriticapital.com" },
  { name: "Clix Capital", domain: "clix.capital" },
  { name: "SMFG India Credit", domain: "smfgindia.com" },
  { name: "Neo Wealth", domain: "neo.group" },
  { name: "360 ONE", domain: "360.one" },
  { name: "Motilal Oswal", domain: "motilaloswal.com" },
  { name: "Nomura India", domain: "nomura.com" },
  { name: "Kotak Investment", domain: "kotakalternate.com" },
  { name: "ICICI Prudential AIF", domain: "icicipruamc.com" },
  { name: "Nippon India", domain: "nipponindiaim.com" },
  { name: "Multiples", domain: "multiplesequity.com" },
  { name: "State Bank of Hyderabad", domain: "sbhyd.co.in" },
  { name: "IDBI Bank", domain: "idbibank.in" },
  { name: "Indian Overseas Bank", domain: "iob.in" },
  { name: "UCO Bank", domain: "ucobank.com" },
  { name: "Central Bank of India", domain: "centralbankofindia.co.in" },
  { name: "Bank of Maharashtra", domain: "bankofmaharashtra.in" },
  { name: "Punjab & Sind Bank", domain: "psbindia.com" },
  { name: "Barclays India", domain: "home.barclays" },
  { name: "MUFG Bank India", domain: "mufg.jp" },
  { name: "Suryoday SFB", domain: "suryodaybank.com" },
  { name: "Utkarsh SFB", domain: "utkarsh.bank" },
  { name: "Jammu & Kashmir Bank", domain: "jkbank.com" },
  { name: "Tamilnad Mercantile", domain: "tmb.in" },
  { name: "DCB Bank", domain: "dcbbank.com" },
  { name: "Ambit Finvest", domain: "ambit.co" },
  { name: "OxyzoFin", domain: "oxyzo.in" },
  { name: "MoneyView", domain: "moneyview.in" },
  { name: "KreditBee", domain: "kreditbee.in" },
  { name: "FlexiLoans", domain: "flexiloans.com" },
  { name: "Indifi", domain: "indifi.com" },
  { name: "NeoGrowth", domain: "neogrowth.in" },
  { name: "Growth Source", domain: "growthsourcefinancial.com" },
  { name: "Arth Digital", domain: "arthdigital.com" },
];

function BankTile({ b }) {
  const [failed, setFailed] = useState(false);
  const monogram = b.name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div
      className="group flex flex-col items-center justify-center gap-2 p-3 h-24 rounded-lg border border-ink/10 bg-white hover:border-[#B8860B]/50 hover:shadow-[0_10px_25px_rgba(31,20,8,0.06)] transition-all"
      title={b.name}
      data-testid={`bank-partner-${b.domain}`}
    >
      {!failed ? (
        <img
          src={`https://logo.clearbit.com/${b.domain}`}
          alt={b.name}
          className="h-8 max-w-[90%] object-contain"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="h-8 w-8 rounded-full bg-[#7A4E1D] text-[#FFF3D9] flex items-center justify-center font-heading text-xs">
          {monogram}
        </div>
      )}
      <div className="text-[10px] font-heading text-ink-muted text-center leading-tight line-clamp-2">{b.name}</div>
    </div>
  );
}

export function BankingPartners() {
  return (
    <section className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-bank-partners">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">
              PARTNERS · BANKS · NBFCs · HFCs · SFBs · PRIVATE CREDIT
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
              100+ Banking Partners.
            </h2>
            <p className="mt-3 text-ink-soft text-base leading-relaxed max-w-2xl">
              We&apos;re integrated with India&apos;s top scheduled commercial banks, PSU banks, NBFCs, housing finance
              companies, small finance banks, foreign banks and private credit funds. One application &mdash; every
              relevant lender pitched on your behalf.
            </p>
          </div>
          <Link to="/venturaz-money/banks" className="text-sm font-heading text-[#7A4E1D] hover:underline inline-flex items-center gap-1" data-testid="bank-partners-view-all">
            View full 114-lender directory <ChevronRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {BANK_PARTNERS.map((b) => <BankTile key={b.domain} b={b} />)}
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
 * Partners Map — 50K+ channel partners across India, rendered on an SVG
 * outline of India with weighted pins on major cities.
 * ==========================================================================
 */

const CITY_PINS = [
  { name: "Delhi",        c: "3.8K", x: 380, y: 175 },
  { name: "Mumbai",       c: "4.6K", x: 285, y: 380 },
  { name: "Bengaluru",    c: "3.2K", x: 385, y: 545 },
  { name: "Chennai",      c: "2.4K", x: 450, y: 585 },
  { name: "Hyderabad",    c: "2.1K", x: 400, y: 470 },
  { name: "Kolkata",      c: "1.9K", x: 555, y: 350 },
  { name: "Pune",         c: "2.2K", x: 315, y: 415 },
  { name: "Ahmedabad",    c: "1.8K", x: 265, y: 305 },
  { name: "Jaipur",       c: "1.4K", x: 320, y: 245 },
  { name: "Lucknow",      c: "1.3K", x: 435, y: 240 },
  { name: "Chandigarh",   c: "1.0K", x: 350, y: 130 },
  { name: "Kochi",        c: "0.9K", x: 355, y: 620 },
  { name: "Bhopal",       c: "0.8K", x: 370, y: 320 },
  { name: "Guwahati",     c: "0.7K", x: 640, y: 290 },
  { name: "Patna",        c: "1.1K", x: 510, y: 275 },
];

export function PartnersMap() {
  const [active, setActive] = useState(null);
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #1F1408 0%, #3E2410 100%)" }} data-testid="raise-debt-partners-map">
      <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-2 text-[#FFF3D9]">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[#E9C46A] mb-2">
            REACH · A BOOTS-ON-GROUND NETWORK
          </div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            50,000+ partners <span className="text-gold">across India.</span>
          </h2>
          <p className="mt-4 text-[#FFF3D9]/85 text-base leading-relaxed max-w-lg">
            DSAs, connectors and financial consultants in every metro and Tier-2 town. If your loan needs
            local paperwork, doorstep pickup or an on-ground property visit &mdash; we have a partner
            within 45 minutes of your pin code.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
            <Stat label="Partners" value="50K+" icon={Users} />
            <Stat label="Cities" value="820+" icon={MapPin} />
            <Stat label="Disbursed FY" value="₹1,240 Cr" icon={IndianRupee} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/venturaz-money/become-partner"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-[#1F1408] text-sm font-heading font-bold hover:bg-yellow-400 shadow-[0_10px_30px_rgba(233,196,106,0.35)]"
              data-testid="partners-map-become-partner"
            >
              <Trophy size={14} /> Become a partner <ArrowRight size={14} />
            </Link>
            <Link to="/money/apply/personal_loan?src=partners-map" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-[#E9C46A]/40 text-[#FFF3D9] text-sm font-heading hover:bg-white/[0.06]" data-testid="partners-map-apply">
              <Award size={14} /> Apply for a loan
            </Link>
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="relative rounded-2xl border border-[#E9C46A]/25 bg-[#2A1B0D] overflow-hidden" data-testid="partners-map-svg-wrap">
            <IndiaOutlineSvg pins={CITY_PINS} active={active} setActive={setActive} />
          </div>
          {active && (
            <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-[#FFF3D9]/80" data-testid="partners-map-hover-info">
              {active.name} · <span className="text-gold">{active.c} partners</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-[#E9C46A]/25 rounded-lg p-3">
      <div className="flex items-center gap-1.5">
        <Icon size={14} className="text-gold" />
        <div className="font-display text-2xl text-gold">{value}</div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFF3D9]/70 mt-1">{label}</div>
    </div>
  );
}

/* --- Inline SVG outline of India (approximate silhouette). Simplified
   from the public-domain SoI blank map — enough to convey shape without
   heavy asset load. Pins are placed in the same 800x700 viewport. -------- */
function IndiaOutlineSvg({ pins, active, setActive }) {
  return (
    <svg viewBox="0 0 800 700" className="w-full h-auto" aria-label="India partners map" data-testid="partners-india-map">
      {/* Simplified India outline path */}
      <path
        d="M300 90 L340 75 L380 80 L410 60 L440 55 L470 70 L500 90 L520 120 L560 150 L580 180 L610 200 L640 240 L660 270 L680 300 L670 320 L640 330 L620 350 L600 370 L580 400 L560 420 L540 450 L520 480 L500 510 L480 540 L450 570 L420 600 L390 620 L370 640 L360 660 L350 660 L340 640 L320 610 L300 580 L285 550 L280 520 L275 490 L270 460 L265 430 L260 400 L255 370 L250 340 L245 310 L240 280 L235 260 L230 240 L235 220 L245 200 L255 180 L265 160 L275 140 L285 120 L295 100 Z"
        fill="#3E2410"
        stroke="#E9C46A"
        strokeOpacity="0.5"
        strokeWidth="1.5"
      />
      {/* State-boundary faint lines */}
      <g stroke="#E9C46A" strokeOpacity="0.14" strokeWidth="0.8" fill="none">
        <path d="M300 200 Q400 210 500 220" />
        <path d="M280 300 Q390 320 520 330" />
        <path d="M280 400 Q380 420 500 430" />
        <path d="M300 500 Q400 520 480 530" />
      </g>
      {/* Pins */}
      {pins.map((p) => {
        const on = active?.name === p.name;
        const r = 6 + Math.min(6, parseFloat(p.c) * 1.2);
        return (
          <g key={p.name}
             onMouseEnter={() => setActive(p)}
             onMouseLeave={() => setActive((a) => (a?.name === p.name ? null : a))}
             onFocus={() => setActive(p)}
             tabIndex={0}
             role="button"
             aria-label={`${p.name} — ${p.c} partners`}
             style={{ cursor: "pointer", outline: "none" }}
             data-testid={`partners-pin-${p.name}`}
          >
            {/* Outer glow */}
            <circle cx={p.x} cy={p.y} r={r + 8} fill="#E9C46A" opacity={on ? 0.35 : 0.18}>
              <animate attributeName="r" values={`${r + 6};${r + 12};${r + 6}`} dur="3s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.05;0.25;0.05" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx={p.x} cy={p.y} r={r} fill="#E9C46A" stroke="#1F1408" strokeWidth="1.5" />
            <text
              x={p.x + r + 6}
              y={p.y + 4}
              fill={on ? "#FFF3D9" : "#FFF3D9"}
              opacity={on ? 1 : 0.85}
              fontSize="12"
              fontFamily="ui-monospace, SFMono-Regular, monospace"
              style={{ textTransform: "uppercase", letterSpacing: 1.4 }}
            >
              {p.name} · {p.c}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
